import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col } from 'react-bootstrap';

/**
 * Reusable export-to-CSV modal with a per-column checkbox picker, all columns
 * selected by default. Same UI/UX as src/components/partials/2-leads/ExportCSVModal.js
 * — extracted here so Advances and Expenses can both use it without duplicating
 * the modal itself, since the columns differ per screen but the picker doesn't.
 *
 * `columns`: [{ label, key, path?, formatter? }] — path is a dot-path into the
 * row object (e.g. 'userInfo.name'); formatter(row, index) takes priority over path.
 */
export default function GenericExportCSVModal({ show, onHide, data, columns, title = 'Export to CSV', filenamePrefix = 'export' }) {
  const [selectedFields, setSelectedFields] = useState(
    columns.reduce((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {})
  );

  useEffect(() => {
    if (show) {
      setSelectedFields(
        columns.reduce((acc, col) => {
          acc[col.key] = true;
          return acc;
        }, {})
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleToggle = (key) => {
    setSelectedFields(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSelectAll = () => {
    setSelectedFields(columns.reduce((acc, col) => { acc[col.key] = true; return acc; }, {}));
  };

  const handleClearAll = () => {
    setSelectedFields(columns.reduce((acc, col) => { acc[col.key] = false; return acc; }, {}));
  };

  const triggerExport = () => {
    const activeColumns = columns.filter(col => selectedFields[col.key]);

    if (activeColumns.length === 0) {
      alert('Please select at least one field to export.');
      return;
    }

    const headers = ['S.No.', ...activeColumns.map(col => col.label)]
      .map(header => `"${String(header).replace(/"/g, '""')}"`)
      .join(',');

    const rows = data.map((row, index) => {
      const snVal = `"${index + 1}"`;
      const rowVals = activeColumns.map(col => {
        let val = '';
        if (col.formatter) {
          val = col.formatter(row, index);
        } else if (col.path) {
          val = col.path.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), row);
        }
        const cellString = (val !== null && val !== undefined) ? String(val) : '';
        return `"${cellString.replace(/"/g, '""')}"`;
      });
      return [snVal, ...rowVals].join(',');
    });

    const csvContent = [headers, ...rows].join('\n');
    const blob = new Blob(
      [new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent],
      { type: 'text/csv;charset=utf-8;' }
    );

    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filenamePrefix}_${new Date().getTime()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ borderBottom: '1px solid #dee2e6' }}>
        <Modal.Title style={{ color: '#ef5a20', fontWeight: 'bold' }}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '20px 30px' }}>
        <div style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
          Select the columns you'd like to include. A total of <strong>{data.length}</strong> rows will be exported based on your current filters.
        </div>

        <div style={{ marginBottom: '20px' }}>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSelectAll}
            style={{ marginRight: '10px', backgroundColor: '#ef5a20', borderColor: '#ef5a20' }}
          >
            Select All
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={handleClearAll}>
            Clear All
          </Button>
        </div>

        <Row style={{ maxHeight: '350px', overflowY: 'auto' }}>
          {columns.map((col) => (
            <Col md={6} key={col.key} style={{ marginBottom: '12px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                background: selectedFields[col.key] ? '#fff7ed' : '#fff'
              }}>
                <input
                  type="checkbox"
                  id={`export-check-${col.key}`}
                  checked={!!selectedFields[col.key]}
                  onChange={() => handleToggle(col.key)}
                  style={{ marginRight: '10px', width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <label
                  htmlFor={`export-check-${col.key}`}
                  style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: '#334155', cursor: 'pointer' }}
                >
                  {col.label}
                </label>
              </div>
            </Col>
          ))}
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={triggerExport}
          style={{ backgroundColor: '#ef5a20', borderColor: '#ef5a20' }}
        >
          📥 Download CSV
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
